import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';
import AppstoreIcon from '@/pages/opinion/assets/svg/channel/app_store.svg';
import GoogleplayIcon from '@/pages/opinion/assets/svg/channel/google_play.svg';
import taptapIcon from '@/pages/opinion/assets/svg/channel/taptap.svg';
import RegionCountryItem from '@/pages/opinion/components/Snippets/RegionCountryItem';
import RatingItem from '../RatingItem';
import './index.scss';
import BubbleCommon, {
  gameStoreGoogleLifetimeTipsText,
  gameStoreAppleStoreTableHeaderTips,
} from '@/pages/opinion/components/BubbleCommon';
import HoverSortCommon from '@/pages/opinion/components/HoverSortCommon';
import { arrayToObj } from '@/pages/opinion/utils';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import { goStatsDetailAction } from '../../../stats/Detail';
import { getLocale, useHistory } from 'umi';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { getTeaLocale } from '@/pages/home/utils/lang';

const { pageable, scrollable, autotip, injectable } = Table.addons;

const tableColumnsConfig = [
  {
    hearText: 'Lifetime. Rating & Reviews',
    key: 'google play-lifetime',
    icon: GoogleplayIcon,
    channel: 'google play',
    tips: gameStoreGoogleLifetimeTipsText,
  },
  {
    hearText: 'Selected AVG. Rating & Reviews',
    key: 'google play-selected',
    channel: 'google play',
    icon: GoogleplayIcon,
    // tips: gameStoresSelectedGoogleText,
  },
  {
    hearText: 'Lifetime Accum. Rating',
    key: 'app store-lifetime',
    channel: 'app store',
    icon: AppstoreIcon,
    tips: undefined,
  },
  {
    hearText: 'Selected AVG. Rating (T-2)',
    key: 'app store-selected',
    channel: 'app store',
    icon: AppstoreIcon,
    tips: gameStoreAppleStoreTableHeaderTips,
  },
  // {
  //   hearText: 'Lifetime Accum. Rating',
  //   key: 'taptap-lifetime',
  //   channel: 'taptap',
  //   icon: taptapIcon,
  //   tips: undefined,
  // },
  // {
  //   hearText: 'Selected AVG. Rating',
  //   key: 'taptap-selected',
  //   channel: 'taptap',
  //   icon: taptapIcon,
  //   tips: undefined,
  // },
];

const compareFunc = (
  value: number,
  standardValue: number,
  symbol: string,
): boolean => {
  const newValue = Math.abs(value || 0);
  switch (symbol) {
    case '<':
      return newValue < standardValue;
    case '<=':
      return newValue <= standardValue;
    case '>':
      return newValue > standardValue;
    case '>=':
      return newValue >= standardValue;
    default:
      return newValue === standardValue;
  }
};

export default function RegionCountryTable({
  dataList,
  selectable,
  filterCountries,
  scoreFilterValue,
  dateType,
  params,
}: any) {
  const [sorts, setSorts] = useState<{
    by: string;
    order: 'asc' | 'desc';
  }>({ by: 'app store-lifetime_reviews', order: 'desc' });

  const [pageInfo, setPageInfo] = useState({ pageIndex: 1, pageSize: 10 });
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const history = useHistory();

  const goToFeeds = (region: string, channel: string) => {
    goStatsDetailAction({
      path: `/opinion/Feeds/Feeds?tab=gameStore`,
      params: {
        statsDetailType: 'feeds-gameStore',
        ...params,
        channel: [channel],
        channel_type: 'comments',
        region: [region],
        region_type: 'market',
      },
      history,
    });
  };

  const getColumnInfo = (data: any, beforeKey: string, channel: string) => {
    let noData = 0;
    let itemParams = [
      'filterSelected',
      'flag',
      'newScore',
      'oldScore',
      'oldTime',
      'newTime',
      'rationPercent',
      'reviews',
      'score',
      'timeLine',
    ].reduce((pre, curKey) => {
      let value = data[beforeKey + '_' + curKey];
      !value && noData++;
      return { ...pre, [curKey]: value };
    }, {});
    return (
      <RatingItem
        {...itemParams}
        reviewClick={() => goToFeeds(data.region, channel)}
        dateType={dateType}
        noData={noData === 9}
        isSelected={selectable}
      />
    );
  };

  const columns = useMemo(() => {
    return tableColumnsConfig.map(({ key, hearText, icon, tips, channel }) => ({
      key: key,
      header: (
        <HoverSortCommon
          sort={sorts}
          sortOptions={formatOptionsMessage([
            { text: 'User Score', value: key + '_score' },
            { text: 'User Score: Change(%)', value: key + '_rationPercent' },
            { text: 'Total Reviews', value: key + '_reviews' },
          ])}
          sortChange={(sort: { by: string; order: 'asc' | 'desc' }) =>
            setSorts(sort)
          }
        >
          <div className="table-header">
            <img src={icon} className="header-icon" />
            <span>{formatMessage({ id: hearText })}</span>
            <BubbleCommon showIcon={!!tips} tipsMap={tips}></BubbleCommon>
          </div>
        </HoverSortCommon>
      ),
      render: (cvm: any) => {
        return getColumnInfo(cvm, key, channel);
      },
    }));
  }, [dataList, selectable, sorts, formatMessage]);

  const dataListMap = useMemo(() => {
    return arrayToObj(dataList, (item) => item.region?.toLocaleLowerCase());
  }, [dataList]);

  const tableRecords = useMemo(() => {
    let localDataList: Array<any> = [];
    let { order, by } = sorts;
    function orderFunc(value: number) {
      return order === 'desc' ? -value : value;
    }

    // 提取出global数据放在table最上一栏,提前拿到，后续不需要过滤
    let globalItem = [];
    let globalIndex = dataList.findIndex(
      (item: { region: string }) => item.region === 'global',
    );
    if (globalIndex > -1) {
      globalItem = dataList.slice(globalIndex, globalIndex + 1);
    }
    // 过滤掉选中的区域
    if (filterCountries.length === 0) {
      localDataList = dataList.filter(
        (item: { region: string }) => item.region !== 'global',
      );
    } else {
      localDataList = filterCountries.map(
        (key: string) =>
          dataListMap[key?.toLocaleLowerCase()] || { region: key },
      );
    }

    // 过滤Alert Countries数据
    if (selectable) {
      localDataList = localDataList.filter((item) => {
        return tableColumnsConfig.some(({ key }) => item[key + '_flag']);
      });
    }

    // 分数过滤器部分
    if (scoreFilterValue) {
      let { valueKey, symbolValue, value } = scoreFilterValue;
      let newValue = Number(value);
      if (!(value === null || isNaN(newValue))) {
        localDataList = localDataList.map((item) => {
          let obj: any = {};
          tableColumnsConfig.forEach(({ key }) => {
            obj[key + '_filterSelected'] = compareFunc(
              item[key + valueKey],
              value,
              symbolValue,
            );
          });
          if (Object.values(obj).filter(Boolean).length > 0) {
            return { ...obj, ...item };
          }
          return null;
        });
        // 过滤null数据
        localDataList = localDataList.filter(Boolean);
      }
    }

    // 进行排序
    localDataList.sort((a, b) => orderFunc((a[by] || 0) - (b[by] || 0)));

    localDataList.unshift(...globalItem);
    return localDataList;
  }, [sorts, dataList, filterCountries, selectable, scoreFilterValue]);

  useEffect(() => {
    setPageInfo({ pageIndex: 1, pageSize: pageInfo.pageSize });
  }, [sorts, scoreFilterValue, filterCountries]);
  const locale = getTeaLocale(getLocale());
  return (
    <CardCommon
      contentCard
      empty={tableRecords?.length === 0}
      emptyHeight={400}
    >
      <ConfigProvider locale={locale}>
        <Table
          bordered="all"
          records={tableRecords}
          className="dbo-table-sticky dbo-regioncountry-table"
          rowClassName={() => 'dbo-regioncountry-table-row'}
          columns={[
            {
              key: 'region',
              header: formatMessage({ id: 'Region/Country' }),
              width: 180,
              fixed: 'left',
              render: (cvm: any) => (
                <div className="dbo-regionlabel">
                  <RegionCountryItem
                    isGlobal
                    market={cvm.region}
                  ></RegionCountryItem>
                </div>
              ),
            },
            ...columns,
          ]}
          addons={[
            pageable({
              ...pageInfo,
              onPagingChange(query) {
                setPageInfo({
                  pageIndex: query.pageIndex || 1,
                  pageSize: query.pageSize || 10,
                });
              },
            }),
            autotip({}),
            scrollable({
              minWidth: 1920,
            }),
            injectable({
              row: (props, context) => {
                return {
                  className:
                    context.recordIndex === 1 ? 'guide-table-two-row-box' : '',
                };
              },
              cell: (props, context) => ({
                className:
                  context.columnIndex === 2 ? 'guide-table-two-cell-box' : '',
              }),
            }),
          ]}
        />
      </ConfigProvider>
    </CardCommon>
  );
}
